import {
  Banner,
  Button,
  ErrorIcon,
  Flex,
  Link,
  Modal,
  Skeleton,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import { useMaskito } from '@maskito/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import type { UpdateCarouselBannerParams } from '@jane/business-admin/data-access';
import {
  useStoreMenuMetadata,
  useStoreSettings,
  useStores,
  useUpdateCarouselBanner,
} from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useDebouncedTrack,
  useModalActionsWithTracking,
} from '@jane/business-admin/hooks';
import {
  EventNames,
  ModalNames,
  SearchSubjects,
  SettingNames,
  normalizePath,
  parseValidationErrors,
  track,
  urlMask,
} from '@jane/business-admin/util';
import type { FilterNames } from '@jane/business-admin/util';
import { StoreSelectModal } from '@jane/shared-b2b/components';

import { ConfirmWrapperWithTracking } from '../../../../ConfirmWrapperWithTracking';
import { SwitchWithBorder } from '../../../../SwitchWithBorder';
import { CarouselSlidesManager } from './CarouselSlidesManager';
import type { CarouselSlide } from './CarouselSlidesManager';
import { ImageUploadArea } from './ImageUploadArea';
import { parseCarouselBanners, updateSlide } from './slideUtils';

const FORM_ERROR_NAME = 'carousel-modal-errors';

const defaultAltText = (storeName?: string) => `${storeName} Marketing Banner`;

export const CarouselModal = ({ closeModal }: { closeModal: () => void }) => {
  const { pathname } = useLocation();
  const { id = '' } = useParams<'id'>();
  const { isFetched: menuDataFetched, data: menuData } =
    useStoreMenuMetadata(id);
  const { data: storePayload, isFetching: storeSettingsLoading } =
    useStoreSettings(id);
  const {
    mutateAsync: updateCarouselBanner,
    isSuccess: updateCarouselBannerSuccess,
  } = useUpdateCarouselBanner(id);

  const catchSubmitErrors = useCatchErrorsWithManager(
    'Error updating carousel banner. Please try again'
  );

  const toast = useToast();
  const stores: (number | string)[] = [];
  const formMethods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      [FORM_ERROR_NAME]: '',
      enabled: menuData?.carousel_banner
        ? menuData?.carousel_banner?.some((banner) => banner.visible)
        : true,
      slides: [] as CarouselSlide[],
      stores,
    },
  });
  const {
    formState: { dirtyFields, isValid },
    setValue,
    clearErrors,
    setError,
    trigger,
  } = formMethods;

  // Initialize with one default slide
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [initialSlides, setInitialSlides] = useState<CarouselSlide[]>([]);
  const [selectedSlidePosition, setSelectedSlidePosition] = useState(1);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isPublishingTo, setIsPublishingTo] = useState(false);

  // ------------ Store Selector -----------
  const {
    modalOpen: publishToModalOpen,
    openModal: setpublishToModalOpen,
    closeModal: setPublishToModalClose,
  } = useModalActionsWithTracking(ModalNames.CarouselPublishTo);

  const onFilterChange = (filterName: FilterNames, value?: string) => {
    if (!value) {
      track({
        event: EventNames.DeselectedFilter,
        filter_name: filterName,
        all_selected_values: [],
        deselected_value: 'all',
        modal_name: ModalNames.CarouselPublishTo,
        url: normalizePath(pathname, id),
      });
      return;
    }

    track({
      event: EventNames.SelectedFilter,
      filter_name: filterName,
      all_selected_values: [value],
      selected_value: value,
      modal_name: ModalNames.CarouselPublishTo,
      url: normalizePath(pathname, id),
    });
  };

  const onToggleAllStores = (selected: boolean) => {
    track({
      event: selected
        ? EventNames.SelectedCheckbox
        : EventNames.DeselectedCheckbox,
      checkbox_label: selected ? 'Select all stores' : 'Deselect all stores',
      modal_name: ModalNames.CarouselPublishTo,
    });
  };

  const onToggleStore = (storeId: string, selected: boolean) => {
    track({
      event: selected
        ? EventNames.SelectedCheckbox
        : EventNames.DeselectedCheckbox,
      checkbox_label: `${selected ? 'Select' : 'Deselect'} store ID ${storeId}`,
      modal_name: ModalNames.CarouselPublishTo,
    });
  };

  // -----------------------

  const selectedSlide = useMemo(
    () => slides.find((slide) => slide.position === selectedSlidePosition),
    [slides, selectedSlidePosition]
  );

  useEffect(() => {
    if (!menuDataFetched) return;

    const { slides: parsedSlides, isEnabled: parsedEnabled } =
      parseCarouselBanners(menuData?.carousel_banner ?? []);

    const areAllSlidesVisible = !!menuData?.carousel_banner.every(
      (slide) => slide.visible
    );

    setSlides(parsedSlides);
    setInitialSlides(parsedSlides);

    setValue('enabled', parsedEnabled && areAllSlidesVisible);
    setIsEnabled(parsedEnabled && areAllSlidesVisible);
  }, [menuDataFetched]);

  const clearFormErrors = () => {
    clearErrors(FORM_ERROR_NAME);
  };

  const setFormError = (message: string) =>
    setError(FORM_ERROR_NAME, { type: 'manual', message });

  const patchCurrentSlide = (patch: Partial<CarouselSlide>) => {
    const updatedSlides = updateSlide(slides, selectedSlidePosition, patch);
    setSlides(updatedSlides);
    setValue('slides', updatedSlides, { shouldDirty: true });
    clearFormErrors();
  };

  const handleSlidesChange = (newSlides: CarouselSlide[]) => {
    setSlides(newSlides);
    setValue('slides', newSlides, { shouldDirty: true });
    clearFormErrors();
  };

  const handleSelectedSlideChange = (slidePosition: number) => {
    setSelectedSlidePosition(slidePosition);
    clearFormErrors();
  };

  const handleDesktopImageChange = (imageUrl: string) => {
    trigger();
    patchCurrentSlide({ desktopImage: imageUrl });
  };

  const handleDesktopImageRemove = () => {
    patchCurrentSlide({ desktopImage: undefined });
  };

  const handleMobileImageChange = (imageUrl: string) => {
    trigger();
    patchCurrentSlide({ mobileImage: imageUrl });
  };

  const handleMobileImageRemove = () => {
    patchCurrentSlide({ mobileImage: undefined, mobileId: undefined });
  };

  const handleLinkChange = (link: string) => {
    patchCurrentSlide({ link: link || undefined });
  };

  const handleAltTextChange = (altText: string) => {
    patchCurrentSlide({ altText });
  };

  const handleRemoveSlide = (slidePosition: number) => {
    if (!slides || slides.length <= 1) return;

    const updatedSlides = slides
      .filter((slide) => slide.position !== slidePosition)
      .map((slide, index) => ({ ...slide, position: index + 1 }));

    setSlides(updatedSlides);

    if (slidePosition === selectedSlidePosition) {
      setSelectedSlidePosition(updatedSlides[0]?.position || 1);
    }
    clearFormErrors();

    track({
      event: EventNames.ModifiedSetting,
      setting_name: SettingNames.CarouselBanner,
      action: 'remove_slide',
      modal_name: ModalNames.Carousel,
      revert: false,
    });
  };

  // ------------ Form Logic ------------
  const validateForm = () => {
    const slidesWithoutImages = slides.flatMap((slide, index) =>
      slide.desktopImage ? [] : [index]
    );
    if (slidesWithoutImages && slidesWithoutImages.length > 0) {
      throw new FormValidationError(FORM_ERROR_NAME, [
        {
          name: FORM_ERROR_NAME,
          message: `Please provide a desktop image for slide${
            slidesWithoutImages.length > 1 ? 's' : ''
          } ${slidesWithoutImages
            .map((i) => i + 1)
            .join(', ')} to publish the carousel.`,
        },
      ]);
    }
  };

  const gatherData = (): UpdateCarouselBannerParams => {
    const carouselBanners: UpdateCarouselBannerParams['carousel_banners'] = [];

    slides.forEach((slide) => {
      if (slide.desktopImage || slide.desktopId) {
        carouselBanners.push({
          id: slide.desktopId,
          position: slide.position,
          breakpoint: 'desktop',
          visible: isEnabled,
          alt_text: slide.altText || defaultAltText(storePayload?.store.name),
          url: slide.link,
          ...(slide.desktopImage?.startsWith('data') && {
            file: slide.desktopImage,
          }),
        });
      }
      if (slide.mobileImage || slide.mobileId) {
        carouselBanners.push({
          id: slide.mobileId,
          position: slide.position,
          breakpoint: 'mobile',
          visible: isEnabled,
          alt_text: slide.altText || defaultAltText(storePayload?.store.name),
          url: slide.link,
          ...(slide.mobileImage?.startsWith('data') && {
            file: slide.mobileImage,
          }),
        });
      }
    });

    // Determine deleted banners by comparing existing API data with current slides
    const existingBanners = menuData?.carousel_banner ?? [];
    const currentIds = new Set<number>(
      carouselBanners.map((b) => (b.id ? b.id : -1)).filter((id) => id !== -1)
    );
    const deletedBannerIds: number[] = existingBanners
      .filter((b) => b.id && !currentIds.has(b.id))
      .map((b) => b.id as number);

    return {
      carousel_banners:
        carouselBanners.length > 0 ? carouselBanners : undefined,
      deleted_carousel_banners:
        deletedBannerIds.length > 0 ? deletedBannerIds : undefined,
    };
  };

  const onSubmit = useCallback(() => {
    validateForm();
    const requestData = gatherData();

    const trackSubmit = (eventProps = {}) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        modal_name: ModalNames.Carousel,
        setting_name: 'Carousel',
        action: 'update',
        url: normalizePath(pathname, id),
        changed_attributes: Object.keys(dirtyFields),
        successful: true,
        ...eventProps,
      });
    };

    const submitMethod = async () => {
      await updateCarouselBanner(requestData);
      trackSubmit();
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSubmit({
          successful: false,
          error_reason: 'InvalidParams',
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: () => {
        trackSubmit({
          successful: false,
          error_reason: 'ServerError',
        });
        throw new Error('Error updating carousel banner. Please try again');
      },
    });
  }, [
    slides,
    isEnabled,
    dirtyFields,
    id,
    pathname,
    catchSubmitErrors,
    menuData?.carousel_banner,
    updateCarouselBanner,
  ]);

  const onSelectStores = (storeIds: number[]) => {
    // NOTE: Initial validation done as part of opening modal

    setValue('stores', storeIds, { shouldDirty: true });
    const requestData = gatherData();
    requestData.stores = storeIds;

    const trackSelectStores = (eventProps = {}) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        modal_name: ModalNames.CarouselPublishTo,
        setting_name: 'Carousel Banner',
        action: 'update',
        url: normalizePath(pathname, id),
        changed_attributes: Object.keys(dirtyFields),
        successful: true,
        ...eventProps,
      });
    };

    const submitMethod = async () => {
      setIsPublishingTo(true);

      try {
        await updateCarouselBanner(requestData);
        trackSelectStores();
      } finally {
        setIsPublishingTo(false);
        setPublishToModalClose();
      }
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSelectStores({
          successful: false,
          error_reason: 'InvalidParams',
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: () => {
        trackSelectStores({
          successful: false,
          error_reason: 'ServerError',
        });

        setFormError('Error updating carousel banner. Please try again');
      },
    });
  };

  useEffect(() => {
    if (updateCarouselBannerSuccess) {
      toast.add({
        label: 'Carousel banner updated.',
        variant: 'success',
      });
      closeModal();
    }
  }, [closeModal, toast, updateCarouselBannerSuccess]);

  const urlRef = useMaskito({ options: urlMask });

  const formErrorMessage =
    (formMethods.formState?.errors?.[FORM_ERROR_NAME]?.message as string) ||
    undefined;

  const hasChanges = useMemo(() => {
    if (initialSlides.length !== slides.length) return true;
    return initialSlides.every((value, index) => value === slides[index]);
  }, [slides, initialSlides]);

  const {
    data: storesData,
    isFetching: isFetchingStores,
    isLoading: isLoadingStores,
  } = useStores();
  const currentStoreSelection =
    storePayload == null ? [] : [storePayload.store.id.toString()];
  const trackSearch = useDebouncedTrack(1000);

  return (
    <ConfirmWrapperWithTracking
      open={true}
      variant="full-screen"
      setOpen={() => closeModal()}
      hasChanges={hasChanges}
      modalName={ModalNames.Carousel}
    >
      <Form.BaseForm
        name="carousel modal"
        onSubmit={onSubmit}
        formMethods={formMethods}
        formErrorName={FORM_ERROR_NAME}
      >
        <Modal.Header
          title="Carousel banner"
          subtitle={storeSettingsLoading ? '' : storePayload?.store.name}
          actions={
            <Flex alignItems="center" gap={24}>
              {formErrorMessage && (
                <Banner
                  variant="error"
                  label={formErrorMessage}
                  icon={<ErrorIcon />}
                />
              )}
              <Flex alignItems="center" gap={16}>
                <SwitchWithBorder
                  name="enabled"
                  label="Visible"
                  onChange={(visible) => {
                    setIsEnabled(visible);
                    clearFormErrors();
                    track({
                      event: EventNames.ToggleVisibility,
                      final_state: visible ? 'visible' : 'hidden',
                      object: 'carousel banner',
                      successful: true,
                      trigger_source_id: 'carousel banner workspace',
                    });
                  }}
                />
                <Button
                  variant="secondary"
                  label="Publish to"
                  ml={16}
                  onClick={() => {
                    try {
                      validateForm();
                      setpublishToModalOpen();
                    } catch (error) {
                      if (error instanceof FormValidationError) {
                        setFormError(error.errors[0].message);
                      } else {
                        throw error;
                      }
                    }
                  }}
                  disabled={!isValid}
                />
                <Form.SubmitButton variant="primary" label="Publish" />
              </Flex>
            </Flex>
          }
        />
        <Modal.Content>
          {menuDataFetched && slides && selectedSlide ? (
            <Flex flexDirection="row">
              <Flex
                flexDirection="column"
                gap={16}
                pr={32}
                pl={32}
                width={460}
                minWidth={460}
              >
                {/* Carousel Slides Management */}
                <CarouselSlidesManager
                  slides={slides}
                  selectedSlidePosition={selectedSlidePosition}
                  onSlidesChange={handleSlidesChange}
                  onSelectedSlideChange={handleSelectedSlideChange}
                />

                <Flex flexDirection="row" justifyContent="space-between">
                  <Typography variant="body-bold">
                    Slide {selectedSlide.position}
                  </Typography>
                  {slides.length > 1 && (
                    <Link
                      onClick={() => handleRemoveSlide(selectedSlide.position)}
                    >
                      Remove
                    </Link>
                  )}
                </Flex>

                <Flex
                  flexDirection="column"
                  background="grays-white"
                  p={16}
                  gap={16}
                  borderRadius="sm"
                >
                  <Form.TextField
                    key={`link-${selectedSlide.position}`}
                    typography="body"
                    name="currentSlideLink"
                    label="Link (Optional)"
                    placeholder="https://"
                    value={selectedSlide.link || ''}
                    onChange={(value: string) => handleLinkChange(value)}
                    ref={urlRef}
                  />
                  <Form.TextAreaField
                    key={`altText-${selectedSlide.position}`}
                    typography="body"
                    rows={3}
                    name="currentSlideAltText"
                    label="Alt text (Optional)"
                    placeholder={defaultAltText(storePayload?.store.name)}
                    value={selectedSlide.altText}
                    onChange={(value: string) => handleAltTextChange(value)}
                  />
                </Flex>
              </Flex>

              <Flex flexDirection="column" width="100%" pr={40} gap={24}>
                <ImageUploadArea
                  key={`desktop-${selectedSlide.position}-${
                    selectedSlide.desktopImage || 'empty'
                  }`}
                  title="Desktop preview"
                  image={selectedSlide.desktopImage}
                  onImageChange={handleDesktopImageChange}
                  onImageRemove={handleDesktopImageRemove}
                  width={1312}
                  height={320}
                  pagination={
                    slides.length > 1
                      ? `${selectedSlide.position}/${slides.length}`
                      : undefined
                  }
                  onValidationError={setFormError}
                />
                <ImageUploadArea
                  key={`mobile-${selectedSlide.position}-${
                    selectedSlide.mobileImage ||
                    selectedSlide.desktopImage ||
                    'empty'
                  }`}
                  title="Mobile preview (Optional)"
                  image={
                    selectedSlide.mobileImage || selectedSlide.desktopImage
                  }
                  onImageChange={handleMobileImageChange}
                  onImageRemove={handleMobileImageRemove}
                  isMobile={true}
                  width={343}
                  height={240}
                  pagination={
                    slides.length > 1
                      ? `${selectedSlide.position}/${slides.length}`
                      : undefined
                  }
                  showFallbackText={
                    !selectedSlide.mobileImage && !!selectedSlide.desktopImage
                  }
                  useFullWidth={true}
                  onValidationError={setFormError}
                />
              </Flex>
            </Flex>
          ) : (
            <Flex p={32}>
              <Skeleton width="100%" height={400}>
                Loading...
              </Skeleton>
            </Flex>
          )}
        </Modal.Content>

        {publishToModalOpen && (
          <StoreSelectModal
            onSearchCallback={(query: string, successful?: boolean) =>
              trackSearch({
                event: EventNames.Search,
                arguments: query,
                subject: SearchSubjects.Stores,
                successful,
              })
            }
            storesData={storesData || []}
            isFetchingStores={isFetchingStores}
            isLoadingStores={isLoadingStores}
            selectedStoreIds={currentStoreSelection}
            disabledStoreIds={currentStoreSelection}
            isSubmitting={isPublishingTo}
            onSubmit={onSelectStores}
            closeModal={() => setPublishToModalClose()}
            onFilterChange={onFilterChange}
            onToggleAll={onToggleAllStores}
            onToggleStore={onToggleStore}
          />
        )}
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};
